import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, AlertTriangle, Tag, ShieldCheck, ClipboardCheck } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd, KeywordCrossLinks } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Mely termékekre kötelező az allergén jelölés?',
    a: '2014. december 13. óta minden nem előrecsomagolt élelmiszerre kötelező az allergén-információ feltüntetése — pékségek, cukrászdák, fagyizók, éttermek és büfék pultjában is, nem csak a gyárilag csomagolt termékeken (EU 1169/2011).',
  },
  {
    q: 'Milyen formában kell feltüntetni az allergéneket?',
    a: 'Írásban (étlapon, terméktáblán, címkén) vagy szóban, ha a felelős személy jelen van és dokumentummal igazolni tudja az információt. Az írásbeli forma — pl. nyomtatott címke vagy táblázat — biztonságosabb, mert nem függ attól, ki van éppen szolgálatban.',
  },
  {
    q: 'Kell-e külön címke a vevő kérésére, helyben csomagolt terméken?',
    a: 'A vevő kérésére, a helyszínen csomagolt termék nem minősül előrecsomagoltnak, így nem kötelező rá külön allergén-címke — de az információt ekkor is biztosítani kell, ha a vásárló kérdezi.',
  },
  {
    q: 'Honnan tudja a rendszer, melyik alapanyag milyen allergént tartalmaz?',
    a: 'Minden alapanyaghoz rögzítve van, hogy a 14 hivatalos EU-allergén közül melyiket tartalmazza. Amikor a receptbe felveszed az alapanyagot, a rendszer automatikusan összesíti a késztermék allergénjeit.',
  },
]

export default function AllergenCimkePage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Allergén jelölés és címke nyomtatás cukrászdáknak — CukrászDoc</title>
        <meta name="description" content="Allergén táblázat és nyomtatható étel-címke automatikusan a receptből — mind a 14 EU-allergén (EU 1169/2011) kötelező jelölésével. Percek alatt kész, hatósági ellenőrzésre kész." />
        <link rel="canonical" href="https://cukraszdoc.hu/allergen-cimke-nyomtatas" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <AlertTriangle size={12} />
            Kötelező 2014 óta minden pultos termékre is
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-white mb-5">
            Allergén jelölés és címke <span style={{ color: TEAL }}>automatikusan, a receptből</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Pékség, cukrászda, fagyizó vagy étterem? A CukrászDoc a recept alapanyagaiból automatikusan azonosítja mind a 14 hivatalos EU-allergént (EU 1169/2011), és felkerül minden dokumentumra — gyártmánylap, HACCP napló és a nyomtatható étel-címke is.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
              Próbáld ki ingyen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Miért kötelező az allergén jelölés?</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            2014. december 13. óta az EU 1169/2011 rendelet alapján az allergén-információ feltüntetése nem csak a gyárilag csomagolt termékekre kötelező, hanem minden nem előrecsomagolt élelmiszerre is — tehát a cukrászda vagy pékség pultjában kínált sütire, tortára, kifliire ugyanúgy vonatkozik, mint egy étterem ételére.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            A tájékoztatás történhet írásban (étlapon, terméktáblán, címkén) vagy szóban — de szóbeli tájékoztatásnál a felelős személynek mindig jelen kell lennie, és dokumentummal kell tudnia igazolni az adatot. Egy nyomtatott, gyártmánylapból generált allergén-táblázat vagy címke ezt a kockázatot veszi le a válladról.
          </p>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: DARK }}>Hogyan kezeli a CukrászDoc az allergéneket?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, title: '14 EU-allergén automatikusan', desc: 'Glutén, tej, tojás, dió és a többi 14 kötelező allergén — alapanyagból kigyűjtve.' },
              { icon: Tag, title: 'Nyomtatható címke', desc: '100×70mm, 50×30mm és A4 (9 db/oldal) méretben, azonnal nyomtatható.' },
              { icon: ShieldCheck, title: 'Gyártmánylapon és HACCP naplóban is', desc: 'Egyszer viszed fel az alapanyagot, minden dokumentumon megjelenik.' },
              { icon: ClipboardCheck, title: 'Nincs kézi utánanézés', desc: 'Nem kell alapanyagonként allergén-táblázatot bújni, a rendszer tudja.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <Icon size={22} style={{ color: TEAL }} className="mb-3" />
                <h3 className="font-semibold mb-2 text-base" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-4" style={{ color: DARK }}>Allergén jelölés 3 lépésben</h2>
          <div className="flex flex-col gap-4 mt-10">
            {[
              'Beírod a recept alapanyagait és mennyiségeit.',
              'A rendszer automatikusan összegyűjti az allergéneket az alapanyag-adatbázisból.',
              'Letöltöd a gyártmánylapot, a HACCP naplót vagy a nyomtatható címkét — allergén jelöléssel.',
            ].map((step, i) => (
              <div key={step} className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-sm" style={{ background: DARK, color: TEAL }}>{i + 1}</div>
                <p className="text-base text-gray-600 pt-1">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések az allergén jelölésről</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <KeywordCrossLinks current="/allergen-cimke-nyomtatas" />

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Kezdd el ma — ingyen</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Regisztrálj, és a recept felvitele után a rendszer automatikusan kigyűjti az allergéneket.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            <Check size={17} /> Ingyenes regisztráció
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
